'use client';

import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { checkFeatureAccess } from '@/lib/ratelimits/actions';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTheme } from 'next-themes';
import html2canvas from 'html2canvas';

const BACKGROUND_COLORS = {
  black: '#0c0a09',
  'dark-gray': '#18181b',
  white: '#ffffff',
  'stone-white': '#e4e4e7',
} as const;

// Reverse mapping for converting hex to color name
const BACKGROUND_COLOR_NAMES = Object.entries(BACKGROUND_COLORS).reduce(
  // biome-ignore lint/performance/noAccumulatingSpread: no other workaround
  (acc, [name, hex]) => ({ ...acc, [hex]: name }),
  {} as Record<string, string>,
);

interface ChartExporterProps {
  chartRef: React.RefObject<HTMLDivElement | null>;
  symbol: string;
  metric: string;
}

export function ChartExporter({
  chartRef,
  symbol,
  metric,
}: ChartExporterProps) {
  const { theme } = useTheme();
  const [textColor, setTextColor] = useState('#f4f4f5'); // Initial white color
  const [bgColor, setBgColor] = useState('#18181b'); // Initial dark gray color
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scale, setScale] = useState('4'); // Default to 4x (ultra HD)
  const [isChecking, setIsChecking] = useState(false);
  const [canExport, setCanExport] = useState<boolean | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const titleText = `${symbol} ${metric}`;

  // Reset state when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      setIsChecking(false);
      setPreviewImage(null);
    } else {
      // Start checking permissions when dialog opens
      checkPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen]);

  const checkPermissions = async () => {
    try {
      setIsChecking(true);
      setCanExport(null); // Reset permission state

      const hasAccess = await checkFeatureAccess('CAN_EXPORT_CHARTS' as const);
      setCanExport(hasAccess);

      if (hasAccess) {
        // Only generate image if user has permission
        await generateImage();
      }
    } catch (error) {
      console.error('Failed to check export permissions:', error);
      toast.error('Failed to check export permissions');
      setCanExport(false);
    } finally {
      setIsChecking(false);
    }
  };

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.crossOrigin = 'anonymous';
      img.src = src;
    });
  };

  const generateImage = async () => {
    if (!chartRef.current) return;

    try {
      // Find the chart visualization container - specifically target the recharts container
      const chartContainer = chartRef.current.querySelector(
        '.recharts-wrapper',
      ) as HTMLElement;

      if (!chartContainer) {
        console.error('Chart container not found');
        return;
      }

      // Create a temporary container to hold just the chart
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.backgroundColor = bgColor;
      tempContainer.style.padding = '20px';

      // Clone the chart container
      const chartClone = chartContainer.cloneNode(true) as HTMLElement;

      // Check if the legend is already inside the chart container
      const legendInChart = chartClone.querySelector(
        '.recharts-legend-wrapper, .recharts-default-legend',
      );

      // Only look for an external legend if it's not already in the chart
      if (!legendInChart) {
        // Find and clone the legend separately (it might be outside the recharts-wrapper)
        const legendElement = chartRef.current.querySelector(
          '.recharts-legend-wrapper, .recharts-default-legend',
        ) as HTMLElement;

        if (legendElement) {
          const legendClone = legendElement.cloneNode(true) as HTMLElement;
          // Position the legend appropriately
          legendClone.style.position = 'relative';
          legendClone.style.left = '0';
          legendClone.style.bottom = '0';
          legendClone.style.marginTop = '10px';

          // Add the legend after adding the chart
          tempContainer.appendChild(chartClone);
          tempContainer.appendChild(legendClone);
        } else {
          // No legend found, just add the chart
          tempContainer.appendChild(chartClone);
        }
      } else {
        // Legend is already in the chart, just add the chart
        tempContainer.appendChild(chartClone);
      }

      // Ensure text elements have the right color
      tempContainer.querySelectorAll('text').forEach((text) => {
        text.setAttribute('fill', textColor);
      });

      // Handle legend items more specifically
      tempContainer
        .querySelectorAll('.recharts-legend-item-text')
        .forEach((item) => {
          if (item instanceof SVGElement) {
            item.style.fill = textColor;
          } else if (item instanceof HTMLElement) {
            item.style.color = textColor;
          }
        });

      // Also handle legend wrapper if it exists
      const legendWrappers = tempContainer.querySelectorAll(
        '.recharts-legend-wrapper, .recharts-default-legend',
      );
      legendWrappers.forEach((wrapper) => {
        // Update any spans or divs inside the legend for text color
        wrapper.querySelectorAll('span, div').forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.color = textColor;
          }
        });
      });

      // Add to body temporarily
      document.body.appendChild(tempContainer);

      // Use html2canvas to capture just the chart and legend
      const canvas = await html2canvas(tempContainer, {
        scale: Number(scale),
        backgroundColor: bgColor,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      // Remove the temporary element
      document.body.removeChild(tempContainer);

      // Create a new canvas for the final image with title and footer
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');

      if (!ctx) throw new Error('Could not get canvas context');

      // Calculate dimensions
      const scaleFactor = Number(scale);
      const titleHeight = 60 * scaleFactor;
      const footerHeight = 40 * scaleFactor;
      const padding = 20 * scaleFactor;

      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height + titleHeight + footerHeight;

      // Fill background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

      // Draw title
      ctx.font = `bold ${
        20 * scaleFactor
      }px Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.fillText(
        titleText,
        finalCanvas.width / 2,
        titleHeight - 20 * scaleFactor,
      );

      // Draw the chart
      ctx.drawImage(canvas, 0, titleHeight);

      // Load and draw logo
      const logo = await loadImage('/images/SP_LOGO_MIN.png');
      const logoHeight = 36 * scaleFactor;
      const logoWidth = (logo.width / logo.height) * logoHeight;

      // Draw "Powered by" section on the right side
      const poweredByY = finalCanvas.height - padding - logoHeight / 2;

      // Use the same font style as the title for consistency
      ctx.font = `bold ${
        16 * scaleFactor
      }px Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = textColor;
      ctx.textAlign = 'right';

      // Calculate positions starting from the right edge
      const rightEdge = finalCanvas.width - padding;

      // First measure all text widths
      const poweredByText = 'Powered by';
      const domainText = 'StockPenguins';
      const poweredByWidth = ctx.measureText(poweredByText).width;
      const domainWidth = ctx.measureText(domainText).width;

      // Calculate positions from right to left
      const domainX = rightEdge;
      const logoX = domainX - domainWidth - 10 * scaleFactor - logoWidth;
      const poweredByX = logoX - 10 * scaleFactor;

      // Draw domain text (rightmost)
      ctx.fillText(domainText, domainX, poweredByY);

      // Draw logo (middle)
      const logoY = poweredByY - logoHeight / 2;
      ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);

      // Draw "Powered by" text (leftmost of the group, but still on right side)
      ctx.fillText(poweredByText, poweredByX, poweredByY);

      // Set the preview image
      setPreviewImage(finalCanvas.toDataURL('image/png'));

      // Store the canvas for download
      canvasRef.current = finalCanvas;
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate chart preview');
    }
  };

  // Regenerate image when settings change
  useEffect(() => {
    if (isDialogOpen && canExport === true && !isChecking) {
      generateImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgColor, scale, isDialogOpen, canExport]);

  const downloadImage = () => {
    if (!previewImage) return;

    const link = document.createElement('a');
    link.download = `${symbol}-${metric.toLowerCase().replace(/\s+/g, '-')}-${
      new Date().toISOString().split('T')[0]
    }.png`;
    link.href = previewImage;
    link.click();
  };

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={(open) => {
        // Prevent closing the dialog during permission check
        if (isChecking && !open) return;
        setIsDialogOpen(open);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-lg">
          <Download className="size-4" />
          <span>Export Chart</span>
        </Button>
      </DialogTrigger>
      {isChecking ? (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-background rounded-lg shadow-lg p-6 max-w-md w-full mx-auto flex flex-col items-center justify-center space-y-6">
            <div className="relative">
              <div className="size-16 border-4 border-muted-foreground/30 border-r-transparent rounded-full animate-spin" />
              <div
                className="size-16 border-4 border-current border-r-transparent rounded-full animate-spin absolute inset-0"
                style={{ animationDuration: '1.5s' }}
              />
            </div>
            <div className="text-center space-y-2">
              <p className="text-xl font-medium animate-pulse">
                Generating Chart Export
              </p>
              <p className="text-sm text-muted-foreground">
                This may take a moment...
              </p>
            </div>
          </div>
        </div>
      ) : (
        <DialogContent className="max-w-3xl">
          {canExport === false ? (
            <>
              <DialogHeader>
                <DialogTitle>Upgrade Required</DialogTitle>
                <DialogDescription className="mt-4">
                  Exporting chart is a premium feature available to all paid
                  plans. Please upgrade to export charts.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button>Upgrade Now</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Export Chart</DialogTitle>
                <DialogDescription>
                  Customize your chart export settings below.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <Select
                      value={BACKGROUND_COLOR_NAMES[bgColor] || 'dark-gray'}
                      onValueChange={(value) => {
                        setBgColor(
                          BACKGROUND_COLORS[
                            value as keyof typeof BACKGROUND_COLORS
                          ],
                        );

                        if (value === 'white' || value === 'stone-white') {
                          setTextColor('#18181b');
                        } else {
                          setTextColor('#f4f4f5');
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="black">Black</SelectItem>
                        <SelectItem value="dark-gray">Dark Gray</SelectItem>
                        <SelectItem value="white">White</SelectItem>
                        <SelectItem value="stone-white">Stone White</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Image Size</Label>
                    <Select value={scale} onValueChange={setScale}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1x (Low Res)</SelectItem>
                        <SelectItem value="2">2x (Medium Res)</SelectItem>
                        <SelectItem value="3">3x (High Res)</SelectItem>
                        <SelectItem value="4">4x (Ultra HD)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Label className="text-lg font-semibold mt-2">
                  Image Preview
                </Label>
                <div
                  className="border border-1 rounded-lg p-4"
                  style={{ backgroundColor: bgColor }}
                >
                  {previewImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={previewImage || '/placeholder.svg'}
                      alt="Chart preview"
                      className="w-full h-auto"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <div className="size-8 border-2 border-current border-r-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  onClick={generateImage}
                  variant="outline"
                  className="mr-2"
                >
                  Refresh Preview
                </Button>
                <Button onClick={downloadImage} disabled={!previewImage}>
                  Download PNG
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
